import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  BackgroundVariant,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from '@dagrejs/dagre';
import {
  Share2,
  Database,
  Hammer,
  Bot,
  Sparkles,
  Radio,
  Search,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Send,
  Zap,
  Globe,
  Hash,
  Tag,
  FolderTree,
  HelpCircle,
  Package,
  ShieldCheck,
  FileText,
  Link2,
  LayoutGrid,
  GitFork,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  graphService,
  type GraphHierarchy,
  type KBNodeListItem,
  type KBGraphEdge,
  type NodeDetail,
} from '@/services/graph.service';
import type { PaginationMeta } from '@/types';
import {
  OrgNode,
  ChannelNode,
  AgentNode,
  KBNode,
  TopicNode,
} from '@/components/knowledge-map/GraphNode';
import { Badge } from '@/components/common/Badge';
import { SlideOverPanel } from '@/components/common/SlideOverPanel';
import { Spinner } from '@/components/common/Spinner';
import { cn } from '@/utils/cn';

// --- Constants ---

const KB_COLORS = [
  '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981',
  '#f59e0b', '#ef4444', '#ec4899', '#14b8a6',
];

const CHANNEL_TYPE_EDGE_COLORS: Record<string, string> = {
  telegram: '#0ea5e9',
  facebook: '#2563eb',
  instagram: '#a855f7',
  custom: '#6366f1',
};

const GRAPH_NODE_LIMIT = 30;

const nodeTypes = {
  orgNode: OrgNode,
  channelNode: ChannelNode,
  agentNode: AgentNode,
  kbNode: KBNode,
  topicNode: TopicNode,
};

const nodeTypeIcons: Record<string, React.ElementType> = {
  topic: Hash,
  entity: Tag,
  category: FolderTree,
  faq: HelpCircle,
  product: Package,
  policy: ShieldCheck,
};

const nodeTypeBadgeVariant: Record<string, string> = {
  topic: 'info',
  entity: 'warning',
  category: 'success',
  faq: 'default',
  product: 'info',
  policy: 'error',
};

const channelTypeIcons: Record<string, React.ElementType> = {
  telegram: Send,
  facebook: Globe,
  instagram: Globe,
  custom: Zap,
};

const EMPTY_PAGINATION: PaginationMeta = {
  totalItems: 0,
  totalPages: 0,
  perPage: 20,
  currentPage: 1,
};

type ViewMode = 'graph' | 'table';

type KBGraphCache = { nodes: KBNodeListItem[]; edges: KBGraphEdge[]; total: number };

// ==================== GRAPH VIEW ====================

// Build org hierarchy flow elements: Org → Channels → Agents → KBs (+ expanded topic nodes)
function buildHierarchyElements(
  hierarchy: GraphHierarchy,
  expandedKBs: Set<string>,
  loadingKBs: Set<string>,
  kbNodesCache: Map<string, KBGraphCache>,
  onToggleExpand: (kbId: string) => void,
) {
  const kbColorMap = new Map<string, string>();
  hierarchy.knowledge_bases.forEach((kb, i) => {
    kbColorMap.set(kb.id, KB_COLORS[i % KB_COLORS.length]);
  });

  const nodeCountMap = new Map<string, number>();
  hierarchy.stats_per_kb.forEach((s) => nodeCountMap.set(s.kb_id, s.node_count));

  const flowNodes: Node[] = [];
  const flowEdges: Edge[] = [];

  const channels = hierarchy.channels || [];
  const channelAgentLinks = hierarchy.channel_agent_links || [];
  const agents = hierarchy.agents || [];
  const agentKbLinks = hierarchy.agent_kb_links || [];

  const agentIdsInChannels = new Set(channelAgentLinks.map((l) => l.agent_id));

  // Org root
  flowNodes.push({
    id: 'org-root',
    type: 'orgNode',
    position: { x: 0, y: 0 },
    data: { label: 'Organization' },
  });

  // Channel nodes
  channels.forEach((ch) => {
    flowNodes.push({
      id: `channel-${ch.id}`,
      type: 'channelNode',
      position: { x: 0, y: 0 },
      data: { label: ch.name, channelType: ch.channel_type, status: ch.status },
    });
    flowEdges.push({
      id: `org-channel-${ch.id}`,
      source: 'org-root',
      target: `channel-${ch.id}`,
      style: { stroke: '#94a3b8', strokeWidth: 2 },
      type: 'default',
    });
  });

  // Agent nodes
  agents.forEach((agent) => {
    flowNodes.push({
      id: `agent-${agent.id}`,
      type: 'agentNode',
      position: { x: 0, y: 0 },
      data: { label: agent.name, agentType: agent.type, status: agent.status },
    });
  });

  // Channel → Agent edges
  channelAgentLinks.forEach((link) => {
    const ch = channels.find((c) => c.id === link.channel_id);
    const edgeColor = ch ? CHANNEL_TYPE_EDGE_COLORS[ch.channel_type] || '#6366f1' : '#6366f1';
    flowEdges.push({
      id: `channel-agent-${link.channel_id}-${link.agent_id}`,
      source: `channel-${link.channel_id}`,
      target: `agent-${link.agent_id}`,
      style: { stroke: edgeColor, strokeWidth: 2 },
      type: 'default',
    });
  });

  // Orphan agents → Org
  agents.forEach((agent) => {
    if (!agentIdsInChannels.has(agent.id)) {
      flowEdges.push({
        id: `org-orphan-agent-${agent.id}`,
        source: 'org-root',
        target: `agent-${agent.id}`,
        style: { stroke: '#d1d5db', strokeWidth: 1.5, strokeDasharray: '4 4' },
        type: 'default',
      });
    }
  });

  // Agent → KB edges
  agentKbLinks.forEach((link) => {
    flowEdges.push({
      id: `agent-kb-${link.agent_id}-${link.knowledge_base_id}`,
      source: `agent-${link.agent_id}`,
      target: `kb-${link.knowledge_base_id}`,
      style: { stroke: '#f59e0b', strokeWidth: 2, strokeDasharray: '6 3' },
      type: 'default',
      animated: true,
    });
  });

  // KB nodes
  hierarchy.knowledge_bases.forEach((kb) => {
    const color = kbColorMap.get(kb.id) || '#6b7280';
    const nodeCount = nodeCountMap.get(kb.id) || 0;
    const isExpanded = expandedKBs.has(kb.id);
    const isLoading = loadingKBs.has(kb.id);

    flowNodes.push({
      id: `kb-${kb.id}`,
      type: 'kbNode',
      position: { x: 0, y: 0 },
      data: {
        label: kb.name,
        description: kb.description,
        kbType: kb.type,
        nodeCount,
        color,
        expanded: isExpanded,
        expandLoading: isLoading,
        onToggleExpand: () => onToggleExpand(kb.id),
      },
    });

    // If expanded, add topic nodes + edges from cache
    if (isExpanded && kbNodesCache.has(kb.id)) {
      const cached = kbNodesCache.get(kb.id)!;

      cached.nodes.forEach((node) => {
        flowNodes.push({
          id: node.id,
          type: 'topicNode',
          position: { x: 0, y: 0 },
          data: {
            label: node.name,
            nodeType: node.type,
            summary: node.summary,
            kbId: kb.id,
            kbName: kb.name,
            color,
          },
        });

        // KB → Topic edge
        flowEdges.push({
          id: `kb-edge-${kb.id}-${node.id}`,
          source: `kb-${kb.id}`,
          target: node.id,
          style: { stroke: color + '40', strokeWidth: 1 },
          type: 'default',
        });
      });

      // Topic ↔ Topic relationship edges
      cached.edges.forEach((edge) => {
        flowEdges.push({
          id: edge.id,
          source: edge.source_node_id,
          target: edge.target_node_id,
          label: edge.relationship_type.replace(/_/g, ' '),
          style: { stroke: '#94a3b8', strokeWidth: 1.5 },
          labelStyle: { fontSize: 10, fill: '#64748b' },
          labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.9 },
          labelBgPadding: [4, 2] as [number, number],
          animated: edge.weight > 0.8,
          type: 'default',
        });
      });
    }
  });

  return { flowNodes, flowEdges };
}

// Dagre layout helper
function getLayoutedElements(nodes: Node[], edges: Edge[]) {
  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 60, ranksep: 90, edgesep: 25 });

  const getNodeDims = (node: Node) => {
    switch (node.type) {
      case 'orgNode':
        return { width: 220, height: 65 };
      case 'channelNode':
        return { width: 170, height: 50 };
      case 'agentNode':
        return { width: 180, height: 60 };
      case 'kbNode':
        return { width: 200, height: 60 };
      default:
        return { width: 150, height: 45 };
    }
  };

  nodes.forEach((node) => {
    const { width, height } = getNodeDims(node);
    g.setNode(node.id, { width, height });
  });

  edges.forEach((edge) => {
    if (g.hasNode(edge.source) && g.hasNode(edge.target)) {
      g.setEdge(edge.source, edge.target);
    }
  });

  dagre.layout(g);

  const layoutedNodes = nodes.map((node) => {
    const pos = g.node(node.id);
    const { width, height } = getNodeDims(node);
    return {
      ...node,
      position: { x: pos.x - width / 2, y: pos.y - height / 2 },
    };
  });

  const nodeIdSet = new Set(nodes.map((n) => n.id));
  const validEdges = edges.filter((e) => nodeIdSet.has(e.source) && nodeIdSet.has(e.target));

  return { nodes: layoutedNodes, edges: validEdges };
}

// Selection highlighting helpers

function getConnectedIds(
  clickedNodeId: string,
  edges: Edge[]
): { nodeIds: Set<string>; edgeIds: Set<string> } {
  const nodeIds = new Set<string>([clickedNodeId]);
  const edgeIds = new Set<string>();

  const childrenMap = new Map<string, Array<{ nodeId: string; edgeId: string }>>();
  const parentMap = new Map<string, Array<{ nodeId: string; edgeId: string }>>();

  for (const e of edges) {
    const kids = childrenMap.get(e.source) || [];
    kids.push({ nodeId: e.target, edgeId: e.id });
    childrenMap.set(e.source, kids);

    const parents = parentMap.get(e.target) || [];
    parents.push({ nodeId: e.source, edgeId: e.id });
    parentMap.set(e.target, parents);
  }

  const walkDown = (id: string) => {
    for (const child of (childrenMap.get(id) || [])) {
      if (!nodeIds.has(child.nodeId)) {
        nodeIds.add(child.nodeId);
        edgeIds.add(child.edgeId);
        walkDown(child.nodeId);
      } else {
        edgeIds.add(child.edgeId);
      }
    }
  };

  const walkUp = (id: string) => {
    for (const parent of (parentMap.get(id) || [])) {
      if (!nodeIds.has(parent.nodeId)) {
        nodeIds.add(parent.nodeId);
        edgeIds.add(parent.edgeId);
        walkUp(parent.nodeId);
      } else {
        edgeIds.add(parent.edgeId);
      }
    }
  };

  walkDown(clickedNodeId);
  walkUp(clickedNodeId);

  return { nodeIds, edgeIds };
}

function applyHighlight(
  nodes: Node[],
  edges: Edge[],
  highlightedNodeIds: Set<string> | null,
  highlightedEdgeIds: Set<string> | null
): { nodes: Node[]; edges: Edge[] } {
  if (!highlightedNodeIds || !highlightedEdgeIds) {
    return {
      nodes: nodes.map((n) => ({ ...n, style: { ...n.style, opacity: 1 } })),
      edges: edges.map((e) => ({
        ...e,
        style: { ...e.style, opacity: 1 },
        animated: (e as any)._originalAnimated ?? e.animated,
      })),
    };
  }

  return {
    nodes: nodes.map((n) => ({
      ...n,
      style: {
        ...n.style,
        opacity: highlightedNodeIds.has(n.id) ? 1 : 0.15,
        transition: 'opacity 0.3s ease',
      },
    })),
    edges: edges.map((e) => ({
      ...e,
      style: {
        ...e.style,
        opacity: highlightedEdgeIds.has(e.id) ? 1 : 0.08,
        strokeWidth: highlightedEdgeIds.has(e.id)
          ? ((e.style?.strokeWidth as number) || 2) + 0.5
          : (e.style?.strokeWidth as number) || 1,
        transition: 'opacity 0.3s ease',
      },
      animated: highlightedEdgeIds.has(e.id) ? true : false,
      _originalAnimated: e.animated,
    })),
  };
}

// --- Org Graph View (inner, requires ReactFlowProvider parent) ---

interface OrgGraphViewInnerProps {
  hierarchy: GraphHierarchy;
  onBuild: () => void;
  building: boolean;
}

const OrgGraphViewInner: React.FC<OrgGraphViewInnerProps> = ({ hierarchy, onBuild, building }) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [nodeDetail, setNodeDetail] = useState<NodeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [expandedKBs, setExpandedKBs] = useState<Set<string>>(new Set());
  const [loadingKBs, setLoadingKBs] = useState<Set<string>>(new Set());
  const kbNodesCacheRef = useRef<Map<string, KBGraphCache>>(new Map());

  const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);

  const [baseNodes, setBaseNodes] = useState<Node[]>([]);
  const [baseEdges, setBaseEdges] = useState<Edge[]>([]);

  const { fitView } = useReactFlow();

  // Toggle KB expand/collapse
  const handleToggleExpand = useCallback(
    async (kbId: string) => {
      if (expandedKBs.has(kbId)) {
        const next = new Set(expandedKBs);
        next.delete(kbId);
        setExpandedKBs(next);
        return;
      }

      if (!kbNodesCacheRef.current.has(kbId)) {
        setLoadingKBs((prev) => new Set(prev).add(kbId));
        try {
          const data = await graphService.getKBGraphNodes(kbId, GRAPH_NODE_LIMIT);
          kbNodesCacheRef.current.set(kbId, data);
        } catch {
          toast.error('Failed to load KB topics');
          setLoadingKBs((prev) => {
            const next = new Set(prev);
            next.delete(kbId);
            return next;
          });
          return;
        }
        setLoadingKBs((prev) => {
          const next = new Set(prev);
          next.delete(kbId);
          return next;
        });
      }

      setExpandedKBs((prev) => new Set(prev).add(kbId));
    },
    [expandedKBs]
  );

  // Rebuild graph elements whenever hierarchy, expanded, or loading changes
  useEffect(() => {
    const { flowNodes, flowEdges } = buildHierarchyElements(
      hierarchy, expandedKBs, loadingKBs, kbNodesCacheRef.current, handleToggleExpand,
    );
    const { nodes: layouted, edges: layoutedEdges } = getLayoutedElements(flowNodes, flowEdges);
    setBaseNodes(layouted);
    setBaseEdges(layoutedEdges);
    setNodes(layouted);
    setEdges(layoutedEdges);
    setTimeout(() => fitView({ padding: 0.2 }), 50);
  }, [hierarchy, expandedKBs, loadingKBs, handleToggleExpand, setNodes, setEdges, fitView]);

  // Apply highlighting when selected node changes
  useEffect(() => {
    if (baseNodes.length === 0) return;

    if (!selectedNodeId) {
      const { nodes: reset, edges: resetEdges } = applyHighlight(baseNodes, baseEdges, null, null);
      setNodes(reset);
      setEdges(resetEdges);
      return;
    }

    const { nodeIds, edgeIds } = getConnectedIds(selectedNodeId, baseEdges);
    const { nodes: highlighted, edges: highlightedEdges } = applyHighlight(baseNodes, baseEdges, nodeIds, edgeIds);
    setNodes(highlighted);
    setEdges(highlightedEdges);
  }, [selectedNodeId, baseNodes, baseEdges, setNodes, setEdges]);

  // Fetch node detail for topic nodes
  useEffect(() => {
    if (
      !selectedNodeId ||
      selectedNodeId.startsWith('kb-') ||
      selectedNodeId.startsWith('agent-') ||
      selectedNodeId.startsWith('channel-') ||
      selectedNodeId === 'org-root'
    ) {
      setNodeDetail(null);
      return;
    }

    let cancelled = false;
    const fetchDetail = async () => {
      setDetailLoading(true);
      try {
        const detail = await graphService.getNodeDetail(selectedNodeId);
        if (!cancelled) setNodeDetail(detail);
      } catch {
        if (!cancelled) setNodeDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    fetchDetail();
    return () => { cancelled = true; };
  }, [selectedNodeId]);

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNodeId((prev) => (prev === node.id ? null : node.id));
  }, []);

  const handleBeautify = useCallback(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(baseNodes, baseEdges);
    setBaseNodes(layoutedNodes);
    setBaseEdges(layoutedEdges);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setSelectedNodeId(null);
    setTimeout(() => fitView({ padding: 0.2 }), 50);
  }, [baseNodes, baseEdges, setNodes, setEdges, fitView]);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setNodeDetail(null);
  }, []);

  // Determine KB name for selected topic node
  const selectedKbName = useMemo(() => {
    if (!selectedNodeId) return '';
    for (const [kbId, data] of kbNodesCacheRef.current.entries()) {
      if (data.nodes.some((n) => n.id === selectedNodeId)) {
        const kb = hierarchy.knowledge_bases.find((k) => k.id === kbId);
        return kb?.name || '';
      }
    }
    return '';
  }, [selectedNodeId, hierarchy]);

  const totalTopicNodes = useMemo(
    () => hierarchy.stats_per_kb.reduce((sum, s) => sum + s.node_count, 0),
    [hierarchy]
  );

  const hasContent = totalTopicNodes > 0 || hierarchy.stats.total_agents > 0 || hierarchy.stats.total_channels > 0;

  if (!hasContent) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <Share2 className="w-16 h-16 mb-4 text-gray-200" />
        <p className="text-lg font-medium text-gray-700">No graph data yet</p>
        <p className="text-sm text-gray-400 mt-1 mb-4">
          {hierarchy.stats.total_kbs > 0
            ? 'Your knowledge bases have content. Build the graph to visualize it.'
            : 'Upload documents to your knowledge bases to build the graph.'}
        </p>
        {hierarchy.stats.total_kbs > 0 && (
          <button
            onClick={onBuild}
            disabled={building}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {building ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Building...</>
            ) : (
              <><Hammer className="w-4 h-4" /> Build Knowledge Graph</>
            )}
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
        <button
          onClick={handleBeautify}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
        >
          <Sparkles className="w-3 h-3" />
          Beautify
        </button>
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        defaultEdgeOptions={{ type: 'default' }}
      >
        <Controls position="bottom-left" />
        <MiniMap
          position="bottom-right"
          nodeStrokeWidth={3}
          zoomable
          pannable
          style={{ width: 150, height: 100 }}
        />
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e5e7eb" />
      </ReactFlow>

      {/* Topic Node Detail Panel */}
      <SlideOverPanel
        isOpen={
          !!selectedNodeId &&
          !selectedNodeId.startsWith('kb-') &&
          !selectedNodeId.startsWith('agent-') &&
          !selectedNodeId.startsWith('channel-') &&
          selectedNodeId !== 'org-root'
        }
        onClose={() => { setSelectedNodeId(null); setNodeDetail(null); }}
        title={nodeDetail?.node.name || 'Loading...'}
        subtitle={selectedKbName}
        size="md"
      >
        {detailLoading ? (
          <div className="flex items-center justify-center py-12"><Spinner size="md" /></div>
        ) : nodeDetail ? (
          <TopicDetailContent detail={nodeDetail} kbName={selectedKbName} />
        ) : null}
      </SlideOverPanel>
    </>
  );
};

// ==================== TABLE VIEW COMPONENTS ====================

// --- Tree Sidebar ---

interface TreeSidebarProps {
  hierarchy: GraphHierarchy;
  selectedKBId: string | null;
  onSelectKB: (kbId: string) => void;
}

const TreeSidebar: React.FC<TreeSidebarProps> = ({ hierarchy, selectedKBId, onSelectKB }) => {
  const [collapsedChannels, setCollapsedChannels] = useState<Set<string>>(new Set());
  const [collapsedAgents, setCollapsedAgents] = useState<Set<string>>(new Set());

  const nodeCountMap = useMemo(() => {
    const m = new Map<string, number>();
    hierarchy.stats_per_kb.forEach((s) => m.set(s.kb_id, s.node_count));
    return m;
  }, [hierarchy.stats_per_kb]);

  const toggleChannel = (id: string) => {
    setCollapsedChannels((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAgent = (id: string) => {
    setCollapsedAgents((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const { channels, agents, knowledge_bases: kbs, agent_kb_links, channel_agent_links } = hierarchy;

  const agentIdsInChannels = useMemo(
    () => new Set(channel_agent_links.map((l) => l.agent_id)),
    [channel_agent_links]
  );
  const kbIdsInAgents = useMemo(
    () => new Set(agent_kb_links.map((l) => l.knowledge_base_id)),
    [agent_kb_links]
  );
  const agentsByChannel = useMemo(() => {
    const m = new Map<string, string[]>();
    channel_agent_links.forEach((l) => {
      const list = m.get(l.channel_id) || [];
      list.push(l.agent_id);
      m.set(l.channel_id, list);
    });
    return m;
  }, [channel_agent_links]);
  const kbsByAgent = useMemo(() => {
    const m = new Map<string, string[]>();
    agent_kb_links.forEach((l) => {
      const list = m.get(l.agent_id) || [];
      list.push(l.knowledge_base_id);
      m.set(l.agent_id, list);
    });
    return m;
  }, [agent_kb_links]);
  const agentMap = useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents]);
  const kbMap = useMemo(() => new Map(kbs.map((kb) => [kb.id, kb])), [kbs]);

  const orphanAgents = useMemo(() => agents.filter((a) => !agentIdsInChannels.has(a.id)), [agents, agentIdsInChannels]);
  const orphanKbs = useMemo(() => kbs.filter((kb) => !kbIdsInAgents.has(kb.id)), [kbs, kbIdsInAgents]);

  const renderKBItem = (kbId: string) => {
    const kb = kbMap.get(kbId);
    if (!kb) return null;
    const count = nodeCountMap.get(kbId) || 0;
    const isSelected = selectedKBId === kbId;
    return (
      <button
        key={kbId}
        onClick={() => onSelectKB(kbId)}
        className={cn(
          'w-full flex items-center gap-2 pl-10 pr-3 py-1.5 text-sm rounded-md transition-colors text-left',
          isSelected ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
        )}
      >
        <Database className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
        <span className="truncate flex-1">{kb.name}</span>
        <span className="text-xs text-gray-400 flex-shrink-0">{count}</span>
      </button>
    );
  };

  const renderAgentGroup = (agentId: string, indent: boolean) => {
    const agent = agentMap.get(agentId);
    if (!agent) return null;
    const agentKbIds = kbsByAgent.get(agentId) || [];
    const isCollapsed = collapsedAgents.has(agentId);
    const hasKBs = agentKbIds.length > 0;

    return (
      <div key={agentId}>
        <button
          onClick={() => hasKBs && toggleAgent(agentId)}
          className={cn(
            'w-full flex items-center gap-2 pr-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors',
            indent ? 'pl-7' : 'pl-3'
          )}
        >
          {hasKBs ? (
            isCollapsed ? <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
          ) : (
            <span className="w-3 flex-shrink-0" />
          )}
          <Bot className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
          <span className="truncate flex-1">{agent.name}</span>
        </button>
        {hasKBs && !isCollapsed && (
          <div className="space-y-0.5">{agentKbIds.map(renderKBItem)}</div>
        )}
      </div>
    );
  };

  return (
    <div className="w-72 border-r border-gray-200 bg-gray-50/50 overflow-y-auto flex-shrink-0">
      <div className="p-3 space-y-1">
        <p className="text-[10px] font-semibold tracking-wider uppercase text-gray-400 px-3 py-1">Hierarchy</p>

        {channels.map((ch) => {
          const ChannelIcon = channelTypeIcons[ch.channel_type] || Zap;
          const isCollapsed = collapsedChannels.has(ch.id);
          const agentIds = agentsByChannel.get(ch.id) || [];
          return (
            <div key={ch.id}>
              <button
                onClick={() => toggleChannel(ch.id)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
              >
                {isCollapsed ? <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />}
                <ChannelIcon className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
                <span className="truncate flex-1">{ch.name}</span>
              </button>
              {!isCollapsed && (
                <div className="space-y-0.5">{agentIds.map((aid) => renderAgentGroup(aid, true))}</div>
              )}
            </div>
          );
        })}

        {orphanAgents.length > 0 && (
          <>
            <p className="text-[10px] font-semibold tracking-wider uppercase text-gray-400 px-3 py-1 mt-3">Unlinked Agents</p>
            {orphanAgents.map((a) => renderAgentGroup(a.id, false))}
          </>
        )}

        {orphanKbs.length > 0 && (
          <>
            <p className="text-[10px] font-semibold tracking-wider uppercase text-gray-400 px-3 py-1 mt-3">Unlinked Knowledge Bases</p>
            {orphanKbs.map((kb) => {
              const count = nodeCountMap.get(kb.id) || 0;
              const isSelected = selectedKBId === kb.id;
              return (
                <button
                  key={kb.id}
                  onClick={() => onSelectKB(kb.id)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors text-left',
                    isSelected ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <Database className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                  <span className="truncate flex-1">{kb.name}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{count}</span>
                </button>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

// --- Topic Detail Content (inside SlideOverPanel) ---

interface TopicDetailContentProps {
  detail: NodeDetail;
  kbName: string;
}

const TopicDetailContent: React.FC<TopicDetailContentProps> = ({ detail, kbName }) => {
  const node = detail.node;
  const Icon = nodeTypeIcons[node.node_type] || FileText;

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-gray-900 break-words">{node.name}</h4>
          <Badge variant={(nodeTypeBadgeVariant[node.node_type] || 'default') as any} className="mt-1">
            {node.node_type}
          </Badge>
        </div>
      </div>

      {node.summary && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Summary</p>
          <p className="text-sm text-gray-700 leading-relaxed">{node.summary}</p>
        </div>
      )}

      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Knowledge Base</p>
        <div className="flex items-center gap-2">
          <Database className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-sm text-gray-700">{kbName}</span>
        </div>
      </div>

      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Linked Chunks</p>
        <p className="text-sm text-gray-700">{detail.chunk_count} chunk{detail.chunk_count !== 1 ? 's' : ''}</p>
      </div>

      {detail.connected_nodes.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
            Connected Nodes ({detail.connected_nodes.length})
          </p>
          <div className="space-y-1.5">
            {detail.connected_nodes.map((cn) => {
              const ConnIcon = nodeTypeIcons[cn.node_type] || FileText;
              const edge = detail.edges.find(
                (e) =>
                  (e.source_node_id === node.id && e.target_node_id === cn.id) ||
                  (e.target_node_id === node.id && e.source_node_id === cn.id)
              );
              return (
                <div key={cn.id} className="flex items-center gap-2 px-2 py-1.5 rounded bg-gray-50 hover:bg-gray-100 transition-colors">
                  <ConnIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-700 truncate flex-1">{cn.name}</span>
                  {edge && (
                    <span className="flex items-center gap-0.5 text-[10px] text-gray-400 flex-shrink-0">
                      <Link2 className="w-2.5 h-2.5" />
                      {edge.relationship_type.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Skeleton Table Rows ---

const SkeletonRows: React.FC = () => (
  <>
    {[...Array(5)].map((_, i) => (
      <tr key={i} className="animate-pulse">
        <td className="px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-200 rounded" />
            <div>
              <div className="h-4 w-36 bg-gray-200 rounded" />
              <div className="h-3 w-48 bg-gray-100 rounded mt-1" />
            </div>
          </div>
        </td>
        <td className="px-6 py-4"><div className="h-5 w-14 bg-gray-200 rounded-full" /></td>
        <td className="px-6 py-4"><div className="h-4 w-8 bg-gray-200 rounded" /></td>
      </tr>
    ))}
  </>
);

// --- View Toggle ---

interface ViewToggleProps {
  viewMode: ViewMode;
  onChangeView: (mode: ViewMode) => void;
}

const ViewToggle: React.FC<ViewToggleProps> = ({ viewMode, onChangeView }) => (
  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
    <button
      onClick={() => onChangeView('graph')}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
        viewMode === 'graph' ? 'bg-white text-gray-900 shadow-[3px_3px_0px_#1a1a2e]' : 'text-gray-500 hover:text-gray-700'
      )}
    >
      <GitFork className="w-3 h-3" />
      Graph
    </button>
    <button
      onClick={() => onChangeView('table')}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
        viewMode === 'table' ? 'bg-white text-gray-900 shadow-[3px_3px_0px_#1a1a2e]' : 'text-gray-500 hover:text-gray-700'
      )}
    >
      <LayoutGrid className="w-3 h-3" />
      Explorer
    </button>
  </div>
);

// ==================== MAIN PAGE ====================

export const KnowledgeMapPage: React.FC = () => {
  const [hierarchy, setHierarchy] = useState<GraphHierarchy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<ViewMode>('graph');
  const [building, setBuilding] = useState(false);

  // Table view state
  const [selectedKBId, setSelectedKBId] = useState<string | null>(null);
  const [tableNodes, setTableNodes] = useState<KBNodeListItem[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>(EMPTY_PAGINATION);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Table detail panel
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [nodeDetail, setNodeDetail] = useState<NodeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // --- Fetch hierarchy on mount ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await graphService.getHierarchy();
        setHierarchy(data);
        // Auto-select first KB with topics for table view
        const firstKb = data.knowledge_bases.find((kb) => {
          const stat = data.stats_per_kb.find((s) => s.kb_id === kb.id);
          return stat && stat.node_count > 0;
        });
        if (firstKb) setSelectedKBId(firstKb.id);
      } catch {
        setError('Failed to load knowledge graph');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // --- Build graphs ---
  const handleBuildGraphs = useCallback(async () => {
    try {
      setBuilding(true);
      toast.loading('Building knowledge graphs... This may take a minute.', { id: 'build-graph' });
      const result = await graphService.buildGraphs();
      const built = result.results.filter((r) => r.status === 'built').length;
      toast.success(`Built graphs for ${built} knowledge base${built !== 1 ? 's' : ''}`, { id: 'build-graph' });

      const data = await graphService.getHierarchy();
      setHierarchy(data);
      setSelectedKBId(null);
      setTableNodes([]);
      setPagination(EMPTY_PAGINATION);
      setSelectedNodeId(null);
    } catch {
      toast.error('Failed to build graphs. Check that a Gemini API key is configured in Settings.', { id: 'build-graph' });
    } finally {
      setBuilding(false);
    }
  }, []);

  // --- Table view: debounce search ---
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // --- Table view: fetch nodes ---
  const fetchTableNodes = useCallback(
    async (page = 1) => {
      if (!selectedKBId || viewMode !== 'table') return;
      setNodesLoading(true);
      try {
        const result = await graphService.getKBNodes(selectedKBId, {
          page,
          limit: 20,
          search: searchTerm || undefined,
        });
        setTableNodes(result.data);
        setPagination(result.pagination);
      } catch {
        toast.error('Failed to load topics');
      } finally {
        setNodesLoading(false);
      }
    },
    [selectedKBId, searchTerm, viewMode]
  );

  useEffect(() => {
    if (viewMode === 'table') fetchTableNodes(1);
  }, [fetchTableNodes, viewMode]);

  // --- Table view: reset when KB changes ---
  const handleSelectKB = useCallback((kbId: string) => {
    setSelectedKBId(kbId);
    setSearchInput('');
    setSearchTerm('');
    setTableNodes([]);
    setPagination(EMPTY_PAGINATION);
    setSelectedNodeId(null);
    setNodeDetail(null);
  }, []);

  // --- Table view: fetch node detail ---
  useEffect(() => {
    if (!selectedNodeId) {
      setNodeDetail(null);
      return;
    }
    let cancelled = false;
    const fetchDetail = async () => {
      setDetailLoading(true);
      try {
        const detail = await graphService.getNodeDetail(selectedNodeId);
        if (!cancelled) setNodeDetail(detail);
      } catch {
        if (!cancelled) setNodeDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    fetchDetail();
    return () => { cancelled = true; };
  }, [selectedNodeId]);

  const selectedKBName = useMemo(() => {
    if (!selectedKBId || !hierarchy) return '';
    return hierarchy.knowledge_bases.find((kb) => kb.id === selectedKBId)?.name || '';
  }, [selectedKBId, hierarchy]);

  const selectedKBTopicCount = useMemo(() => {
    if (!selectedKBId || !hierarchy) return 0;
    return hierarchy.stats_per_kb.find((s) => s.kb_id === selectedKBId)?.node_count || 0;
  }, [selectedKBId, hierarchy]);

  const totalTopicNodes = useMemo(
    () => hierarchy?.stats_per_kb.reduce((sum, s) => sum + s.node_count, 0) || 0,
    [hierarchy]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] text-gray-500">
        <Share2 className="w-12 h-12 mb-4 text-gray-300" />
        <p className="text-lg font-medium">{error}</p>
        <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600 transition-colors">
          Retry
        </button>
      </div>
    );
  }

  const stats = hierarchy?.stats;

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[#1a1a2e] bg-white flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-emerald-600" />
              Knowledge Map
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {viewMode === 'graph'
                ? 'Interactive visualization of your organization hierarchy'
                : 'Explore your organization\'s knowledge graph'}
            </p>
          </div>
          {stats && (
            <div className="flex items-center gap-4">
              {stats.total_channels > 0 && (
                <div className="flex items-center gap-1.5 text-sm text-gray-600">
                  <Radio className="w-4 h-4 text-sky-500" />
                  <span className="font-medium">{stats.total_channels}</span>
                  <span className="text-gray-400">channel{stats.total_channels !== 1 ? 's' : ''}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Bot className="w-4 h-4 text-amber-500" />
                <span className="font-medium">{stats.total_agents}</span>
                <span className="text-gray-400">agent{stats.total_agents !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Database className="w-4 h-4 text-emerald-500" />
                <span className="font-medium">{stats.total_kbs}</span>
                <span className="text-gray-400">KB{stats.total_kbs !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Hash className="w-4 h-4 text-emerald-500" />
                <span className="font-medium">{totalTopicNodes}</span>
                <span className="text-gray-400">topics</span>
              </div>
              <ViewToggle viewMode={viewMode} onChangeView={setViewMode} />
              <button
                onClick={handleBuildGraphs}
                disabled={building}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50"
              >
                {building ? (
                  <div className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Hammer className="w-3 h-3" />
                )}
                {building ? 'Building...' : 'Rebuild'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content Area */}
      {viewMode === 'graph' ? (
        /* ===== GRAPH VIEW: Full-page org hierarchy ===== */
        <div className="flex-1 relative">
          <ReactFlowProvider>
            <OrgGraphViewInner
              hierarchy={hierarchy!}
              onBuild={handleBuildGraphs}
              building={building}
            />
          </ReactFlowProvider>
        </div>
      ) : (
        /* ===== TABLE VIEW: Sidebar + paginated table ===== */
        <div className="flex-1 flex overflow-hidden">
          <TreeSidebar hierarchy={hierarchy!} selectedKBId={selectedKBId} onSelectKB={handleSelectKB} />

          <div className="flex-1 flex flex-col overflow-hidden bg-white">
            {!selectedKBId ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Database className="w-12 h-12 mb-3 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">Select a Knowledge Base</p>
                <p className="text-xs text-gray-400 mt-1">Choose a KB from the sidebar to view its topics</p>
              </div>
            ) : (
              <>
                {/* Content Header with Search */}
                <div className="px-6 py-4 border-b border-[#1a1a2e] flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{selectedKBName}</h2>
                      <p className="text-sm text-gray-500">{selectedKBTopicCount} topics</p>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search topics..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="w-64 h-9 pl-9 pr-4 bg-white border-2 border-[#1a1a2e] rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-gray-50 border-b border-[#1a1a2e]">
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Topic</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Type</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Connections</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {nodesLoading ? (
                        <SkeletonRows />
                      ) : tableNodes.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-6 py-16 text-center">
                            <div className="text-gray-400">
                              <Hash className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                              <p className="text-sm font-medium text-gray-500">No topics found</p>
                              {searchTerm && (
                                <button
                                  onClick={() => { setSearchInput(''); setSearchTerm(''); }}
                                  className="text-xs text-emerald-600 hover:text-emerald-700 mt-1"
                                >
                                  Clear search
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        tableNodes.map((node) => {
                          const Icon = nodeTypeIcons[node.type] || FileText;
                          return (
                            <tr
                              key={node.id}
                              onClick={() => setSelectedNodeId(node.id)}
                              className={cn(
                                'hover:bg-gray-50 cursor-pointer transition-colors',
                                selectedNodeId === node.id && 'bg-emerald-50/50'
                              )}
                            >
                              <td className="px-6 py-3.5">
                                <div className="flex items-center gap-2.5">
                                  <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{node.name}</p>
                                    {node.summary && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{node.summary}</p>}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-3.5">
                                <Badge variant={(nodeTypeBadgeVariant[node.type] || 'default') as any}>{node.type}</Badge>
                              </td>
                              <td className="px-6 py-3.5">
                                <span className="text-sm text-gray-600 flex items-center gap-1">
                                  <Link2 className="w-3.5 h-3.5 text-gray-400" />
                                  {node.connection_count}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-3 border-t border-[#1a1a2e] bg-gray-50/50 flex-shrink-0">
                    <span className="text-sm text-gray-500">
                      Showing {(pagination.currentPage - 1) * pagination.perPage + 1}&ndash;{Math.min(pagination.currentPage * pagination.perPage, pagination.totalItems)} of {pagination.totalItems}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => fetchTableNodes(pagination.currentPage - 1)}
                        disabled={pagination.currentPage <= 1}
                        className="p-1.5 rounded-md border-2 border-[#1a1a2e] text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-gray-600 min-w-[60px] text-center">
                        {pagination.currentPage} / {pagination.totalPages}
                      </span>
                      <button
                        onClick={() => fetchTableNodes(pagination.currentPage + 1)}
                        disabled={pagination.currentPage >= pagination.totalPages}
                        className="p-1.5 rounded-md border-2 border-[#1a1a2e] text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Table View Detail Slide-Over */}
          <SlideOverPanel
            isOpen={!!selectedNodeId}
            onClose={() => { setSelectedNodeId(null); setNodeDetail(null); }}
            title={nodeDetail?.node.name || 'Loading...'}
            subtitle={selectedKBName}
            size="md"
          >
            {detailLoading ? (
              <div className="flex items-center justify-center py-12"><Spinner size="md" /></div>
            ) : nodeDetail ? (
              <TopicDetailContent detail={nodeDetail} kbName={selectedKBName} />
            ) : null}
          </SlideOverPanel>
        </div>
      )}
    </div>
  );
};
